import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsMicrosoft, BsShieldCheck } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Panel from '#globalComponents/Overlay/Panel';

import { startOAuthFlow } from '../../../api/emailAccountsService';

const PROVIDER_CONFIGS = {
	outlook: {
		name: 'Outlook / Office 365',
		icon: BsMicrosoft,
		description: 'Verbinden Sie Ihr Microsoft-Konto sicher mit OAuth2',
	},
};

const OAuthFlowStep = ({ provider, onSuccess, onBack, onClose }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isConnecting, setIsConnecting] = useState(false);

	const config = PROVIDER_CONFIGS[provider];
	const ProviderIcon = config.icon;

	const handleConnect = async () => {
		setIsLoading(true);
		setError(null);
		setIsConnecting(true);

		try {
			// Get authorization URL from backend
			const { authUrl } = await startOAuthFlow(provider);

			// Open OAuth flow in popup window
			const width = 600;
			const height = 700;
			const left = window.screen.width / 2 - width / 2;
			const top = window.screen.height / 2 - height / 2;

			const popup = window.open(
				authUrl,
				'OAuth Authorization',
				`width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
			);

			if (!popup) {
				throw new Error('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
			}

			// Poll popup window for completion
			const checkInterval = setInterval(() => {
				if (popup.closed) {
					clearInterval(checkInterval);
					setIsLoading(false);
					setIsConnecting(false);

					// Check if OAuth was successful by querying the backend
					// The backend should have created the account if successful
					// We'll let the parent component handle refetching and closing
					onSuccess();
				}
			}, 500);

			// Listen for message from popup (alternative to polling)
			const handleMessage = (event) => {
				// Verify origin for security
				if (event.origin !== window.location.origin) {
					return;
				}

				if (event.data.type === 'oauth-success') {
					clearInterval(checkInterval);
					popup.close();
					setIsLoading(false);
					setIsConnecting(false);
					window.removeEventListener('message', handleMessage);
					onSuccess();
				} else if (event.data.type === 'oauth-error') {
					clearInterval(checkInterval);
					popup.close();
					setIsLoading(false);
					setIsConnecting(false);
					setError(event.data.error || 'Fehler beim Verbinden des Kontos');
					window.removeEventListener('message', handleMessage);
				}
			};

			window.addEventListener('message', handleMessage);

			// Timeout after 5 minutes
			setTimeout(
				() => {
					if (!popup.closed) {
						clearInterval(checkInterval);
						popup.close();
						setIsLoading(false);
						setIsConnecting(false);
						setError('OAuth-Timeout: Bitte versuchen Sie es erneut');
						window.removeEventListener('message', handleMessage);
					}
				},
				5 * 60 * 1000,
			);
		} catch (err) {
			setError(err.message || 'Fehler beim Starten des OAuth-Flows');
			setIsLoading(false);
			setIsConnecting(false);
		}
	};

	return (
		<>
			<Panel.Header subtitle={config.description} onClose={onClose}>
				{config.name} verbinden
			</Panel.Header>

			<Panel.Body>
				<div className="space-y-4">
					<Card className="flex flex-col items-center gap-4 p-6 text-center">
						<div className="flex h-9 w-9 items-center justify-center rounded-control bg-primary-100 text-primary-600">
							<ProviderIcon className="h-4 w-4" />
						</div>

						{!isConnecting && !error && (
							<div>
								<p className="text-sm font-semibold text-text">
									Klicken Sie auf &quot;Verbinden&quot;, um Ihr {config.name}-Konto zu autorisieren
								</p>
								<p className="mt-1 text-xs text-bgBase-800">
									Sie werden zu {config.name} weitergeleitet, um die Berechtigung zu erteilen
								</p>
							</div>
						)}

						{isConnecting && (
							<div>
								<p className="text-sm font-semibold text-text">Warte auf Autorisierung …</p>
								<p className="mt-1 text-xs text-bgBase-800">
									Bitte schließen Sie den Autorisierungsprozess im Popup-Fenster ab
								</p>
							</div>
						)}

						{error && (
							<Alert tone="warning" title="Verbindung fehlgeschlagen" className="w-full text-left">
								{error}
							</Alert>
						)}

						<Button variant="solid" size="md" onClick={handleConnect} loading={isLoading}>
							Mit {config.name} verbinden
						</Button>
					</Card>

					<Alert tone="info" icon={BsShieldCheck} title="Sichere Authentifizierung mit OAuth2">
						<ul className="ml-4 list-disc space-y-0.5">
							<li>Keine Passwort-Weitergabe notwendig</li>
							<li>Jederzeit widerrufbare Berechtigungen</li>
							<li>Automatische Token-Erneuerung</li>
							<li>Höchste Sicherheitsstandards</li>
						</ul>
					</Alert>
				</div>
			</Panel.Body>

			<Panel.Footer>
				<Button variant="outline" size="md" onClick={onBack} disabled={isLoading}>
					Zurück zur Provider-Auswahl
				</Button>
			</Panel.Footer>
		</>
	);
};

OAuthFlowStep.propTypes = {
	provider: PropTypes.oneOf(['outlook']).isRequired,
	onSuccess: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
	onClose: PropTypes.func,
};

export default OAuthFlowStep;
